import { useEffect, useRef, useCallback } from 'react'
import { useLoadFrillWidgetScript } from 'util/hooks/frillWidget'

// Widget setup for React: https://github.com/Frill-co/frill-widget-examples/blob/master/react/src/components/FrillWidget.tsx
// API doc: https://github.com/Frill-co/frill-widget-examples/tree/master/api
export const useFrillWidget = (
  widgetKey,
  currentUser,
  setAnnouncementCount
) => {
  const widget = useRef()

  useLoadFrillWidgetScript()

  const handleClickLauncher = useCallback(() => {
    if (widget.current?.toggle) {
      widget.current.toggle()
    }
  }, [])

  useEffect(
    () => {
      if (!currentUser?.email || !widgetKey) return
      const config = {
        key: widgetKey,
        callbacks: {
          // This will be called when the widget is loaded
          onReady: frillWidget => {
            widget.current = frillWidget
            frillWidget.identify({
              email: currentUser.email,
              name: `${currentUser.first_name} ${currentUser.last_name}`,
            })
          },
          onBadgeCount: ({ count }) => {
            if (setAnnouncementCount) {
              setAnnouncementCount(count)
            }
          },
        },
        settings: {
          // Because we are using our own launcher, we need to use the `null` launcher type
          launcher: {
            type: 'null',
          },
        },
      }

      const isWidgetCreated = window.Frill_Config?.some(
        c => c.key === widgetKey || widget.current
      )

      if (!isWidgetCreated) {
        if ('Frill' in window) {
          widget.current = window.Frill.widget(config)
        } else {
          window.Frill_Config = window.Frill_Config || []
          window.Frill_Config.push(config)
        }
      }
    },
    [widgetKey, currentUser, setAnnouncementCount]
  )

  useEffect(
    () => {
      return () => {
        // Check if there is an active widget
        if (widget.current?.destroy) {
          // If there is a widget, destroy it, this will remove all event listeners and nodes added
          // to the DOM by the widget
          widget.current.destroy()
          widget.current = null
        }
        // We also need to remove our config from the list so it doesn't get initialised.
        if (window.Frill_Config) {
          window.Frill_Config = window.Frill_Config.filter(
            c => c.key !== widgetKey
          )
        }
      }
    },
    [widgetKey]
  )

  return handleClickLauncher
}
